<template>
  <div v-if="hasContractOrQuotationLicense" ref="sentinel">
    <HoverMenu width="360" :nudgeamount="-3" :delay="300" @activatorclick="openFirstContract">
      <template v-slot:menu-activator>
        <v-card flat :color="backgroundColor" dark>
          <v-row no-gutters align="center" class="flex-nowrap">
            <v-btn icon :color="activatorColor" small>
              <v-progress-circular v-if="loading" indeterminate :size="size ? size - 6 : 18" :width="2"
                :color="activatorColor">
              </v-progress-circular>
              <v-icon v-else :size="size ? size : 24" :class="activatorIconClass" v-text="'mdi-file-certificate'" />
            </v-btn>
          </v-row>
        </v-card>
      </template>
      <template v-slot:menu-content>
        <v-card>
          <div v-if="loading" class="d-flex justify-center py-2">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div v-else-if="!hasContracts" class="text-center pa-4">
            {{ $t('servicesystem.common_objects.fallbackterms.no_machine_contract') }}
          </div>
          <div v-else>
            <h4 class="pa-2">{{ $t('servicesystem.common_objects.machine.contracted_to') }}</h4>
            <v-divider class="mx-2" />
            <v-list dense class="py-0 pb-1">
              <v-list-item v-for="detail in contracts" :key="detail.Id" class="px-2" :to="contractRoute(detail)">
                <v-list-item-icon class="my-auto mr-2">
                  <v-icon size="20">
                    {{ getContractTypeIcon(detail.ContractType.Id) }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content class="py-1">
                  <v-list-item-title>
                    <a class="text-decoration-none">
                      {{ detail.ContractId }}
                    </a>
                    {{ detail.Name }}
                  </v-list-item-title>
                  <v-list-item-title>
                    <v-row no-gutters align="center" justify="space-between">
                      <div>
                        <v-chip v-if="detail.ContractType && detail.ContractType.Name" small style="border-radius: 4px;"
                          class="px-2">
                          {{ detail.ContractType.Name }}
                        </v-chip>
                      </div>
                      <div class="caption ml-2" :class="detailStatusClass(detail)" v-if="detailStatus(detail)">
                        {{ detailStatus(detail) }}
                      </div>
                    </v-row>
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-icon class="my-auto ml-2">
                  <v-icon>
                    mdi-chevron-right
                  </v-icon>
                </v-list-item-icon>
              </v-list-item>
            </v-list>
          </div>
        </v-card>
      </template>
    </HoverMenu>
  </div>
</template>


<script>
import clientAPI from "@/api/ClientAPIMixin.js";
import HoverMenu from "@/components/Menus/HoverMenu.vue";
import ContractMixin from "@/helpers/Mixins/ContractMixin.js";
import LicencesMixin from "@/helpers/Mixins/LicencesMixin.js";
import LicenseKeys from "@/helpers/Constants/LicenseKeys";

export default {
  name: "MachineContractIcon",
  props: {
    machine: Object,
    size: Number,
  },
  components: {
    HoverMenu,
  },
  mixins: [clientAPI, ContractMixin, LicencesMixin],

  data() {
    return {
      loading: false,
      contracts: null,
      hasLoaded: false,
      io: null,
    };
  },

  mounted() {
    if (!this.hasContractOrQuotationLicense) {
      return;
    }
    this.setupIntersectionObserver();
  },

  beforeDestroy() {
    if (this.io) {
      try {
        this.io.disconnect();
      } catch (e) { /* noop */ }
      this.io = null;
    }
  },

  methods: {
    setupIntersectionObserver() {
      if (!this.hasContractOrQuotationLicense) return;
      // If already loaded, skip
      if (this.hasLoaded) return;

      // If no IntersectionObserver, fallback to immediate fetch
      if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
        this.fetch();
        return;
      }

      const options = {
        root: null,
        rootMargin: '100px', // start a bit before entering viewport
        threshold: 0.01,
      };

      this.io = new IntersectionObserver((entries) => {
        const entry = entries && entries[0];
        if (entry && entry.isIntersecting && !this.hasLoaded) {
          this.hasLoaded = true;
          this.fetch();
          if (this.io) {
            try {
              this.io.disconnect();
            } catch (e) { /* noop */ }
            this.io = null;
          }
        }
      }, options);

      const target = this.$refs && this.$refs.sentinel;
      if (target && target instanceof Element) {
        this.io.observe(target);
      } else {
        // Fallback if ref not ready
        this.$nextTick(() => {
          const t2 = this.$refs && this.$refs.sentinel;
          if (t2 && t2 instanceof Element && this.io) {
            this.io.observe(t2);
          } else {
            this.fetch(); // ultimate fallback
          }
        });
      }
    },

    async fetch() {
      if (!this.hasContractOrQuotationLicense) {
        return;
      }
      if (!this.machine || !this.machine.Id) {
        this.contracts = null;
        return;
      }
      this.loading = true;
      try {
        this.contracts = await this.getMachineContractsById(this.machine.Id);
      } catch (e) {
        console.error(e);
        this.contracts = null;
      } finally {
        this.loading = false;
      }
    },

    openFirstContract() {
      if (!this.hasContractOrQuotationLicense) return;
      if (!this.hasContracts) return;

      const route = this.contractRoute(this.displayContract);
      this.$router.push(route);
    },

    isPast(date) {
      if (!date) return false;
      const d = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return d.getTime() < today.getTime();
    },

    isWithinSixMonths(date) {
      if (!date) return false;
      const now = new Date();
      const target = new Date(date);
      const sixMonthsFromNow = new Date(now.getFullYear(), now.getMonth() + 6, now.getDate());
      return target.getTime() >= now.getTime() && target.getTime() <= sixMonthsFromNow.getTime();
    },

    contractRoute(detail) {
      var route;
      if (!this.showNewContractViews) {
        route = {
          name: 'OldContractView',
          params: {
            contractId: detail.ContractId,
            contractTypeId: detail.Contract.ContractTypeId,
          }
        };
      }
      else {
        route = {
          name: this.getViewRouteFromContractType(detail.Contract.ContractTypeId), //from mixin
          params: {
            contractId: detail.ContractId,
            contractTypeId: detail.Contract.ContractTypeId,
          },
        };
      }
      return route;
    },

    detailStatus(detail) {
      // Only show time-based detail for monthly billing (type 0)
      const isMonthly = detail && detail.ContractType && detail.ContractType.Type === 0;
      if (!isMonthly) return '';

      const terminated = detail && detail.ContractTerminated;
      const endDate = detail && detail.ContractEnd;

      if (terminated) {
        // Future termination date -> show date
        if (!this.isPast(terminated)) {
          return `Terminates ${this.i18nFormatDate(terminated)}`;
        }
        // Already terminated
        return 'Terminated';
      }

      if (endDate) {
        // Ended in the past
        if (this.isPast(endDate)) {
          return `Expired`;
        }
        return `Expires ${this.i18nFormatDate(endDate)}`;
      }

      return '';
    },

    detailStatusClass(detail) {
      const isMonthly = detail && detail.ContractType && detail.ContractType.Type === 0;
      if (!isMonthly) return 'grey--text text--darken-1';

      const terminated = detail && detail.ContractTerminated;
      const endDate = detail && detail.ContractEnd;

      // ended
      if (endDate && this.isPast(endDate)) {
        return 'error--text';
      }

      // termination scheduled or end within 6 months
      if ((terminated && this.isWithinSixMonths(terminated)) || (endDate && this.isWithinSixMonths(endDate))) {
        return 'warning--text';
      }

      return 'grey--text text--darken-1';
    },
  },

  computed: {
    hasContractOrQuotationLicense() {
      return this.checkIfLicencedByKey(`${LicenseKeys.Contract},${LicenseKeys.Quotation}`);
    },
    showNewContractViews() {
      return this.$store.state.GlobalSettings.ShowNewContractViews;
    },

    activatorColor() {
      const d = this.displayContract;
      if (!d) return 'disabled';

      // inactive contract
      if (!d.Contract || d.Contract.Active === false) return 'disabled';

      // invalid detail
      if (d.ContractValid === false) return 'error';

      const isMonthly = d && d.ContractType && d.ContractType.Type === 0;
      if (isMonthly) {
        // terminated in the past
        if (d.ContractTerminated && this.isPast(d.ContractTerminated)) return 'disabled';
        // expired (end date in the past)
        if (d.ContractEnd && this.isPast(d.ContractEnd)) return 'error';
        // Do NOT warn for within 6 months; treat as active
      }

      // active and valid
      return 'primary';
    },

    displayContract() {
      if (!this.hasContracts) return null;
      // Prefer active + valid, otherwise first
      const sorted = [...this.contracts].sort((a, b) => {
        const aScore = (a?.Contract?.Active ? 2 : 0) + (a?.ContractValid ? 1 : 0);
        const bScore = (b?.Contract?.Active ? 2 : 0) + (b?.ContractValid ? 1 : 0);
        return bScore - aScore;
      });
      return sorted[0];
    },

    hasContracts() {
      return Array.isArray(this.contracts) && this.contracts.length > 0;
    },

    backgroundColor() {
      return 'transparent';
    },

    activatorIconClass() {
      const d = this.displayContract;
      if (!d) return [];
      // Only apply termination styling for monthly billing (type 0)
      const isMonthly = d && d.ContractType && d.ContractType.Type === 0;
      if (!isMonthly) return [];
      if (d.ContractTerminated && this.isPast(d.ContractTerminated)) {
        return ['terminated-icon'];
      }
      return [];
    },
  },

  watch: {
    machine: {
      handler() {
        // reset and re-observe for lazy load
        this.hasLoaded = false;
        if (this.io) {
          try { this.io.disconnect(); } catch (e) {
            //ignore
          }
          this.io = null;
        }
        this.setupIntersectionObserver();
      },
      deep: false,
    },
  },
};
</script>


<style lang="scss" scoped>
.terminated-icon {
  color: #4a4a4a !important;
  text-decoration: line-through;
}
</style>
